import React, { useMemo } from 'react';
import clsx from 'clsx';
import s from './BuyBlock.module.scss';
import sg from '../../styles/global.module.scss';
import { IonIcon } from '@ionic/react';
import Button from '../Buttons/Button/Button';
import ButtonMicroWrapper from '../Buttons/ButtonMicroWrapper/ButtonMicroWrapper';
import { cartOutline, heartOutline, mailOutline } from 'ionicons/icons';
import Centralizer from '../Centralizer/Centralizer';
import { NullableNumber } from '../../utils/types';
import {
  CurrenciesEnum,
  CURRENCIES_CONFIG,
  PriceTypeEnum,
} from '../../models/product';
import {
  ContactInfoType,
  useContactInfoContext,
} from '../ContactInfoModal/context';
import {
  AnalyticEventsEnum,
  getProductAnalyticData,
  trackEvent,
} from '../../Analytics';

// RETURN BACK WHEN MULTICURRENCY READY
const exchangeRate = 16000;
function convertToUSD(price: number) {
  const usdPrice = price / exchangeRate;
  return usdPrice > 50 ? Math.round(usdPrice / 10) * 10 : Math.round(usdPrice);
}

const getPriceString = (
  _from: NullableNumber,
  _to: NullableNumber,
  _currency: CurrenciesEnum,
  isPreview: boolean
) => {
  if (!_from && !_to) return ['', '-'];

  // TODO ASAP: RETURN BACK
  const [from, to] = [
    _currency === 'IDR' && _from ? convertToUSD(_from) : _from,
    _currency === 'IDR' && _to ? convertToUSD(_to) : _to,
  ];
  const currency = _currency === 'IDR' ? 'USD' : _currency;
  //

  const symbol = CURRENCIES_CONFIG[currency];

  const formatter = new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    // TODO RETURN BACK
    // notation: isPreview && currency === 'IDR' ? 'compact' : 'standard',
    // maximumFractionDigits: 1, // usefull for IDR mostly, example: 1.4 mln
    //
    maximumFractionDigits: 0,
    trailingZeroDisplay: 'stripIfInteger',
  } as any);

  const priceFrom = from
    ? formatter.format(Number(from)).replace(currency, symbol).trim()
    : null;

  const priceTo = to
    ? formatter.format(Number(to)).replace(currency, symbol).trim()
    : null;

  let firstLine = '';
  let secondLine = '';

  if (priceFrom && priceTo) {
    firstLine = priceFrom + ' -';
    secondLine = priceTo;
  } else if (priceFrom) {
    firstLine = 'from';
    secondLine = priceFrom;
  } else if (priceTo) {
    firstLine = 'up to';
    secondLine = priceTo;
  }

  return [firstLine, secondLine];
};

export type BuyBlockProps = {
  priceFrom: NullableNumber;
  priceTo: NullableNumber;
  priceCurrency: CurrenciesEnum;
  priceType: PriceTypeEnum | null;
  isPreview: boolean;
  contactInfoContextData: ContactInfoType;
};

const BuyBlock: React.FC<BuyBlockProps> = ({
  priceFrom,
  priceTo,
  priceCurrency,
  isPreview,
  priceType,
  contactInfoContextData,
}) => {
  const priceLines = useMemo(
    () => getPriceString(priceFrom, priceTo, priceCurrency, isPreview),
    [priceFrom, priceTo, priceCurrency, isPreview]
  );

  const noPrice = !priceFrom && !priceTo;

  const { setInfo } = useContactInfoContext();
  const openContactModal = () => setInfo(contactInfoContextData);

  //

  const analyticData = useMemo(() => {
    if (contactInfoContextData.product) {
      return getProductAnalyticData(contactInfoContextData.product);
    } else {
      return {};
    }
  }, [contactInfoContextData.product]);

  return (
    <div
      className={clsx(
        s.BuyBlock__container,
        !isPreview ? s.BuyBlock__container__ProductDetailPage : ''
      )}
    >
      <Centralizer enableVerticalCentralization>
        <div
          className={clsx(sg.Stack_Horizontal, sg.Gap_5)}
          style={{ width: '100%' }}
        >
          <div
            className={clsx(
              sg.Stack_Horizontal,
              s.Price_Wrapper,
              isPreview ? s.Price_Wrapper__preview : ''
            )}
          >
            {noPrice ? (
              <p className={clsx(s.Text__payment)} style={{ fontSize: 16 }}>
                No price info
              </p>
            ) : (
              <>
                <div>
                  <p className={clsx(s.Text_Price)}>{priceLines[0]}</p>
                  <p className={clsx(s.Text_Price)}>{priceLines[1]}</p>
                </div>

                <div className={clsx(sg.Stack_Horizontal)}>
                  <div
                    style={{
                      borderLeft: 'solid 1.2px white',
                      height: '30px',
                      marginLeft: '7px',
                      marginRight: '5px',
                      transform: 'rotate(10deg)',
                    }}
                  />

                  <div>
                    <p className={clsx(s.Text__payment)}>per</p>
                    <p className={clsx(s.Text__payment)}>
                      {priceType === PriceTypeEnum.PER_DAY
                        ? 'event'
                        : priceType === PriceTypeEnum.PER_HOUR
                        ? 'hour'
                        : '?'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Like  */}
          {/* <ButtonMicroWrapper
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              openContactModal();
            }}
          >
            <IonIcon icon={heartOutline} className={clsx(s.IonIcon)} />
          </ButtonMicroWrapper> */}

          <div style={{ height: '45px' }} />

          {!isPreview && (
            <ButtonMicroWrapper
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                openContactModal();
                await trackEvent(AnalyticEventsEnum.CONTACT);
                await trackEvent(
                  AnalyticEventsEnum.CLICK__PRODUCT_CONTACT,
                  analyticData
                );
              }}
              className={clsx(s.IonIcon__wrapper__message)}
            >
              <IonIcon icon={mailOutline} className={clsx(s.IonIcon)} />
            </ButtonMicroWrapper>
          )}

          {/* {isPreview && (
            <ButtonMicroWrapper
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openContactModal();
              }}
            >
              <IonIcon icon={cartOutline} className={clsx(s.IonIcon)} />
            </ButtonMicroWrapper>
          )} */}

          {!isPreview && (
            <Button
              width="90%"
              text="Book now"
              color="secondary"
              fill="solid"
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                openContactModal();
                await trackEvent(AnalyticEventsEnum.INITIATE_CHEKOUT);
                await trackEvent(
                  AnalyticEventsEnum.CLICK__PRODUCT_BOOKING,
                  analyticData
                );
              }}
            />
          )}
        </div>
      </Centralizer>
    </div>
  );
};

export default BuyBlock;
